import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import moment from "moment";
import { getNotificacionesOrdenPago } from "./ApiServices";

const etiquetaTipo = (tipo) => {
  const t = String(tipo || "").toLowerCase();
  if (t === "reenvio") return "Reenvío";
  if (t === "generacion") return "Generación";
  return tipo || "—";
};

const formatearFecha = (value) => {
  if (!value) return "—";
  const m = moment(value);
  return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : "—";
};

export default function ModalHistorialNotificacionesOrdenPago({
  open,
  onClose,
  idOrdenPago,
  nroOp = "",
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notificaciones, setNotificaciones] = useState([]);

  const cargar = useCallback(async () => {
    if (!idOrdenPago) return;
    setLoading(true);
    setError("");
    try {
      const data = await getNotificacionesOrdenPago(idOrdenPago);
      setNotificaciones(
        Array.isArray(data.notificaciones) ? data.notificaciones : []
      );
    } catch (err) {
      setError(err.message || "No se pudo cargar el historial de envíos");
      setNotificaciones([]);
    } finally {
      setLoading(false);
    }
  }, [idOrdenPago]);

  useEffect(() => {
    if (open && idOrdenPago) {
      cargar();
    } else if (!open) {
      setNotificaciones([]);
      setError("");
    }
  }, [open, idOrdenPago, cargar]);

  const titulo = nroOp
    ? `Historial de envíos — OP ${nroOp}`
    : "Historial de envíos";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{titulo}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={cargar}>
                Reintentar
              </Button>
            }
          >
            {error}
          </Alert>
        ) : notificaciones.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No hay registros de envío para esta orden de pago.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Destinatario</TableCell>
                  <TableCell>Usuario</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notificaciones.map((notif) => (
                  <TableRow key={notif.id ?? `${notif.created_at}-${notif.tipo}`}>
                    <TableCell>
                      {formatearFecha(notif.created_at ?? notif.fecha_envio)}
                    </TableCell>
                    <TableCell>{etiquetaTipo(notif.tipo)}</TableCell>
                    <TableCell>
                      <Chip
                        label={notif.mail_enviado ? "Enviado" : "No enviado"}
                        size="small"
                        color={notif.mail_enviado ? "success" : "warning"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{notif.destinatario || "—"}</TableCell>
                    <TableCell>{notif.usuario || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
